"use client";

import { useCallback, useState, useTransition } from "react";
import CollectionFilterBar from "@/components/CollectionFilterBar";
import CollectionFilterPanel from "@/components/CollectionFilterPanel";
import type { ActiveFilters } from "@/components/CollectionFilterPanel";
import Pagination from "@/components/Pagination";
import ProductCard from "@/components/ProductCard";
import Section from "@/components/Section";
import Text from "@/components/Text";
import { filterProducts } from "@/app/(frontend)/faith-in-action/[category]/actions";
import classNames from "@/helpers/classNames";
import { ViewItemListTracker } from "@/tools/analytics";
import type { ProductLike } from "@/tools/analytics";
import type { FilterOptions, StallContext } from "@/tools/medusa";
import {
  getProductImages,
  getProductPricing,
  getSanityProductTags,
  getProductVariants,
} from "@/tools/medusa/helpers/productCard";
import type { ProductOptionLabels } from "@/helpers/productOptionLabels";

import styles from "./styles.module.scss";

interface ProductListingProps {
  categoryHandle: string;
  initialProducts: any[];
  filterOptions: FilterOptions;
  initialTotalPages: number;
  heading?: string;
  stallContext?: StallContext;
  optionLabels?: ProductOptionLabels;
  /** Page number the route was entered at (1 for the base URL, N for `/page/N`). */
  initialPage?: number;
  /**
   * Base path for crawlable SSG pagination links. When set and no filters are
   * active, pagination renders as `<Link>` to `${basePath}` for page 1 and
   * `${basePath}page/N/` for subsequent pages. When filters are active,
   * pagination falls back to in-place client-side refetch.
   */
  pageHrefBase?: string;
}

const defaultFilters = (filterOptions: FilterOptions): ActiveFilters => ({
  sort: "newest",
  categoryIds: [],
  typeIds: [],
  priceMin: filterOptions.priceMin,
  priceMax: filterOptions.priceMax,
});

const filtersToParams = (filters: ActiveFilters, filterOptions: FilterOptions) => {
  const params = new URLSearchParams();
  if (filters.sort && filters.sort !== "newest") {
    params.set("sort", filters.sort);
  }
  if (filters.categoryIds.length) {
    params.set("categories", filters.categoryIds.join(","));
  }
  if (filters.typeIds.length) {
    params.set("type", filters.typeIds.join(","));
  }
  if (filters.priceMin !== filterOptions.priceMin) {
    params.set("price_min", String(filters.priceMin));
  }
  if (filters.priceMax !== filterOptions.priceMax) {
    params.set("price_max", String(filters.priceMax));
  }
  return params;
};

const productsToTrackable = (products: any[]): ProductLike[] =>
  products
    .map((p) => {
      const { price } = getProductPricing(p);
      return {
        id: (p.id as string) ?? (p.handle as string),
        title: (p.title as string) ?? "",
        // Medusa's native product type relation, not `metadata.product_type`.
        // The latter is part of our `ProductMetadata` interface but has never
        // been populated on a real product — every product in production has
        // null metadata — so this was always sending an undefined type to GA4.
        productType: (p.type?.value as string | undefined) ?? undefined,
        price: price ?? 0,
      };
    })
    .filter((p) => Boolean(p.id) && Boolean(p.title));

const ProductListing = (props: ProductListingProps) => {
  const {
    categoryHandle,
    initialProducts,
    filterOptions,
    initialTotalPages,
    heading,
    stallContext,
    optionLabels,
    initialPage = 1,
    pageHrefBase,
  } = props;

  const [isPending, startTransition] = useTransition();
  const [products, setProducts] = useState(initialProducts);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(initialTotalPages);
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>(defaultFilters(filterOptions));
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const fetchProducts = useCallback(
    (filters: ActiveFilters, page: number) => {
      startTransition(async () => {
        const result = await filterProducts({
          categoryHandle,
          categoryIds: filters.categoryIds.length ? filters.categoryIds : undefined,
          typeIds: filters.typeIds.length ? filters.typeIds : undefined,
          sort: filters.sort as any,
          priceMin: filters.priceMin !== filterOptions.priceMin ? filters.priceMin : undefined,
          priceMax: filters.priceMax !== filterOptions.priceMax ? filters.priceMax : undefined,
          page,
        });

        setProducts(result.products);
        setTotalPages(result.totalPages);
        setCurrentPage(result.page);

        const params = filtersToParams(filters, filterOptions);
        if (page > 1) {
          params.set("page", String(page));
        }
        const qs = params.toString();
        window.history.replaceState(null, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
      });
    },
    [categoryHandle, filterOptions],
  );

  const handleApplyFilters = useCallback(
    (filters: ActiveFilters) => {
      setActiveFilters(filters);
      fetchProducts(filters, 1);
    },
    [fetchProducts],
  );

  const handleRemoveCategory = useCallback(
    (categoryId: string) => {
      const updated = {
        ...activeFilters,
        categoryIds: activeFilters.categoryIds.filter((c) => c !== categoryId),
      };
      setActiveFilters(updated);
      fetchProducts(updated, 1);
    },
    [activeFilters, fetchProducts],
  );

  const handleRemoveType = useCallback(
    (typeId: string) => {
      const updated = {
        ...activeFilters,
        typeIds: activeFilters.typeIds.filter((t) => t !== typeId),
      };
      setActiveFilters(updated);
      fetchProducts(updated, 1);
    },
    [activeFilters, fetchProducts],
  );

  const handleResetPrice = useCallback(() => {
    const updated = {
      ...activeFilters,
      priceMin: filterOptions.priceMin,
      priceMax: filterOptions.priceMax,
    };
    setActiveFilters(updated);
    fetchProducts(updated, 1);
  }, [activeFilters, fetchProducts, filterOptions]);

  const handleSortChange = useCallback(
    (sort: string) => {
      const updated = { ...activeFilters, sort };
      setActiveFilters(updated);
      fetchProducts(updated, 1);
    },
    [activeFilters, fetchProducts],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      fetchProducts(activeFilters, page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [activeFilters, fetchProducts],
  );

  // Only use crawlable Link-based pagination when the listing is showing its
  // default server-rendered product set. Filters and non-default sort are
  // client-state only, so switching pages needs to stay client-side.
  const hasActiveFilters =
    activeFilters.categoryIds.length > 0 ||
    activeFilters.typeIds.length > 0 ||
    activeFilters.sort !== "newest" ||
    activeFilters.priceMin !== filterOptions.priceMin ||
    activeFilters.priceMax !== filterOptions.priceMax;

  const getPageHref =
    pageHrefBase && !hasActiveFilters
      ? (page: number) => (page <= 1 ? pageHrefBase : `${pageHrefBase}page/${page}/`)
      : undefined;

  return (
    <>
      <ViewItemListTracker
        products={productsToTrackable(products)}
        listId={categoryHandle}
        listName={heading ?? categoryHandle}
      />
      <Section name="product-listing" theme="lighter" removeTopSpacing className={styles.section}>
        {heading && (
          <Text as="h1" text={heading} variant="heading" size="xl" className={styles.heading} />
        )}

        <CollectionFilterBar
          filterOptions={filterOptions}
          activeFilters={activeFilters}
          onOpenFilters={() => setIsFilterOpen(true)}
          onRemoveCategory={handleRemoveCategory}
          onRemoveType={handleRemoveType}
          onResetPrice={handleResetPrice}
          onSortChange={handleSortChange}
        />

        {/* The cards are H3s, which sat straight under the page's H1. A heading
            for the grid fills the level for a screen reader without showing. */}
        <Text as="h2" text="Products" className={styles.visuallyHidden} />

        <div className={classNames(styles.grid, { [styles.loading]: isPending })}>
          {products.map((product: any) => {
            const { thumbnail, hoverImage } = getProductImages(product);
            const { price, originalPrice } = getProductPricing(product);
            return (
              <ProductCard
                key={product.id}
                handle={product.handle}
                title={product.title}
                price={price}
                originalPrice={originalPrice}
                thumbnail={thumbnail}
                hoverImage={hoverImage}
                tags={getSanityProductTags(product)}
                variants={getProductVariants(product)}
                stallContext={stallContext}
                optionLabels={optionLabels}
              />
            );
          })}
        </div>

        {products.length === 0 && !isPending && (
          <div className={styles.empty}>
            <Text as="p" text="No products match your filters." color="themeFgMuted" />
          </div>
        )}

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          getPageHref={getPageHref}
          className={styles.pagination}
        />
      </Section>

      <CollectionFilterPanel
        show={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApply={handleApplyFilters}
        filterOptions={filterOptions}
        initialFilters={activeFilters}
      />
    </>
  );
};

export default ProductListing;
export type { ProductListingProps };
